import React, { useState, useEffect, useCallback } from 'react';
import { toViewerZone, viewerTz } from '../utils/timeZone';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { AlertTriangle, Calendar, Clock, User, Loader2, CheckCircle2, SkipForward } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status options (excluding Scheduled since it's the current state)
const STATUS_OPTIONS = [
  { value: 'Attended', label: 'Attended', color: 'bg-green-500' },
  { value: 'Rescheduled', label: 'Rescheduled', color: 'bg-blue-500' },
  { value: 'No Show', label: 'No Show', color: 'bg-red-500' }
];

const AppointmentStatusAlert = () => {
  const { user } = useAuth();
  const userRef = React.useRef(user);
  userRef.current = user;
  const [alerts, setAlerts] = useState([]);
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [showRescheduleForm, setShowRescheduleForm] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    date: '',
    start_time: '',
    end_time: ''
  });

  const currentAlert = alerts[currentAlertIndex];

  // Fetch pending alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/appointments/pending-status-alerts`);
      const pendingAlerts = (response.data.alerts || []).map(a => toViewerZone(a, viewerTz(userRef.current)));
      
      if (pendingAlerts.length > 0 && !showDialog) {
        setAlerts(pendingAlerts);
        setCurrentAlertIndex(0);
        setShowDialog(true);
        setSelectedStatus('');
        setShowRescheduleForm(false);
      }
    } catch (error) {
      // Silently fail - don't show errors for polling
      console.debug('Failed to check appointment alerts:', error);
    }
  }, [showDialog]);

  // Poll for alerts every 60 seconds
  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  // Handle status selection
  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
    if (status === 'Rescheduled') {
      setShowRescheduleForm(true);
      // Set default reschedule date to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setRescheduleData({
        date: format(tomorrow, 'yyyy-MM-dd'),
        start_time: currentAlert?.start_time || '09:00',
        end_time: currentAlert?.end_time || '09:30'
      });
    } else {
      setShowRescheduleForm(false);
    }
  };

  // Submit status update
  const handleSubmit = async () => {
    if (!selectedStatus) {
      toast.error('Please select a status');
      return;
    }

    if (selectedStatus === 'Rescheduled') {
      if (!rescheduleData.date || !rescheduleData.start_time) {
        toast.error('Please enter the rescheduled date and time');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        status: selectedStatus
      };

      if (selectedStatus === 'Rescheduled') {
        payload.rescheduled_to_date = rescheduleData.date;
        payload.rescheduled_to_time = rescheduleData.start_time;
        payload.rescheduled_to_end_time = rescheduleData.end_time;
      }

      await axios.post(`${API}/appointments/${currentAlert.id}/update-status`, payload);
      
      toast.success(`Appointment marked as ${selectedStatus}`);

      // Move to next alert or close dialog
      if (currentAlertIndex < alerts.length - 1) {
        setCurrentAlertIndex(prev => prev + 1);
        setSelectedStatus('');
        setShowRescheduleForm(false);
      } else {
        setShowDialog(false);
        setAlerts([]);
        setCurrentAlertIndex(0);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update appointment status');
    } finally {
      setSaving(false);
    }
  };

  // Handle bypass - dismiss all pending alerts permanently and close modal
  const handleBypass = async () => {
    // Dismiss all pending alerts on the backend
    try {
      const dismissPromises = alerts.map(alert => 
        axios.post(`${API}/appointments/${alert.id}/dismiss-status-alert`)
          .catch(err => {
            console.error(`Failed to dismiss alert for appointment ${alert.id}:`, err);
            return null; // Continue with other dismissals even if one fails
          })
      );
      
      await Promise.all(dismissPromises);
    } catch (error) {
      console.error('Error dismissing alerts:', error);
    }
    
    // Close the modal and clear all alerts
    setShowDialog(false);
    setAlerts([]);
    setCurrentAlertIndex(0);
    setSelectedStatus('');
    setShowRescheduleForm(false);
  };

  // Format time for display
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), 'EEEE, MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (!showDialog || !currentAlert) {
    return null;
  }

  return (
    <Dialog open={showDialog} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-lg" 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton={true}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            Appointment Status Required
          </DialogTitle>
          <DialogDescription>
            This appointment was scheduled for {formatTime(currentAlert.start_time)} and requires a status update.
            {alerts.length > 1 && (
              <span className="ml-2 text-xs">
                ({currentAlertIndex + 1} of {alerts.length} pending)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Appointment Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">{currentAlert.participant_name || 'Unknown Participant'}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-white">
                {currentAlert.appointment_type_label || currentAlert.appointment_type}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span>{formatDate(currentAlert.date)}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>{formatTime(currentAlert.start_time)}{currentAlert.tz_abbr && !currentAlert.end_time ? ` ${currentAlert.tz_abbr}` : ''}</span>
              {currentAlert.end_time && (
                <span className="text-muted-foreground">- {formatTime(currentAlert.end_time)}{currentAlert.tz_abbr ? ` ${currentAlert.tz_abbr}` : ''}</span>
              )}
            </div>
          </div>

          {/* Status Selection */}
          <div className="space-y-2">
            <Label className="text-base font-medium">What was the outcome?</Label>
            <div className="grid grid-cols-3 gap-2">
              {STATUS_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  variant={selectedStatus === option.value ? 'default' : 'outline'}
                  className={`w-full ${selectedStatus === option.value ? option.color : ''}`}
                  onClick={() => handleStatusSelect(option.value)}
                  data-testid={`status-btn-${option.value.toLowerCase().replace(' ', '-')}`}
                >
                  {selectedStatus === option.value && <CheckCircle2 className="w-4 h-4 mr-1" />}
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Reschedule Form */}
          {showRescheduleForm && (
            <div className="space-y-3 p-4 border rounded-lg bg-blue-50 border-blue-200">
              <Label className="font-medium text-blue-800">Reschedule To:</Label>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="reschedule-date" className="text-sm">New Date *</Label>
                  <Input
                    id="reschedule-date"
                    type="date"
                    value={rescheduleData.date}
                    onChange={(e) => setRescheduleData(prev => ({ ...prev, date: e.target.value }))}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    data-testid="reschedule-date-input"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="reschedule-time" className="text-sm">New Time *</Label>
                  <Input
                    id="reschedule-time"
                    type="time"
                    value={rescheduleData.start_time}
                    onChange={(e) => setRescheduleData(prev => ({ ...prev, start_time: e.target.value }))}
                    data-testid="reschedule-time-input"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="reschedule-end-time" className="text-sm">End Time (optional)</Label>
                <Input
                  id="reschedule-end-time"
                  type="time"
                  value={rescheduleData.end_time}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, end_time: e.target.value }))}
                  data-testid="reschedule-end-time-input"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={handleBypass}
            disabled={saving}
            className="w-full sm:w-auto order-2 sm:order-1"
            data-testid="bypass-status-btn"
          >
            <SkipForward className="w-4 h-4 mr-2" />
            Bypass
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedStatus || saving}
            className="w-full sm:flex-1 order-1 sm:order-2"
            data-testid="submit-status-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              `Confirm ${selectedStatus || 'Status'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentStatusAlert;
